import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Icons, cx } from '../../components/ui.tsx';
import { useSheetCreation } from '../sheets/useSheetCreation.ts';
import { useDocumentCreation } from '../documents/useDocumentCreation.ts';

/**
 * One New button, the way a drive has one.
 *
 * The toolbar had five controls doing this job — New folder, New sheet with
 * its own arrow, New document with another, Upload, and an unlabelled folder
 * glyph — which is five things to read before making one thing, and far too
 * many for a phone. Everything that creates or brings in now lives behind a
 * single button, in the order a drive puts them: make a folder, bring
 * something in, start something new.
 *
 * The template lists are a second panel rather than a hover-out submenu.
 * Hover submenus are a mouse gesture, and half of what this office does
 * happens on a phone; a panel that slides to the templates and back works the
 * same under a finger as under a pointer.
 */

type Panel = 'root' | 'sheet' | 'document';

interface Props {
  folderId: string | null;
  onError(message: string): void;
  /** Opens the ordinary file picker. */
  onUploadFiles(): void;
  /** Opens the directory picker. */
  onUploadFolder(): void;
  onNewFolder(): void;
}

export function NewMenu({ folderId, onError, onUploadFiles, onUploadFolder, onNewFolder }: Props) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<Panel>('root');
  const root = useRef<HTMLDivElement>(null);

  const sheets = useSheetCreation(folderId, onError);
  const documents = useDocumentCreation(folderId);

  const importInput = useRef<HTMLInputElement>(null);

  // Closing returns to the first panel, so the menu never reopens halfway
  // down a branch the user has forgotten they were in.
  function close() {
    setOpen(false);
    setPanel('root');
  }

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape steps back a panel before it closes, which is what the arrow
      // that got you here implies.
      if (panel !== 'root') setPanel('root');
      else close();
    };

    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) close();
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
    };
  }, [open, panel]);

  const run = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <div className="relative" ref={root}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        className="inline-flex h-control items-center gap-1.5 rounded-md bg-[var(--blue-600)] px-3 text-body-sm font-medium text-white shadow-sm transition-fast hover:bg-[var(--blue-700)]"
      >
        <Icons.Plus />
        New
        <span className="text-[10px] opacity-80" aria-hidden>
          ▾
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          /*
            Anchored to the button's right edge, not its left.

            The New button sits at the right-hand end of the toolbar, so a
            menu growing rightwards ran off the side of a phone. Growing
            leftwards from the same corner fits on both, and the width cap
            covers the case where the toolbar itself is narrower than the
            menu.
          */
          className="absolute right-0 z-50 mt-1 w-[19rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border-strong bg-surface py-1 shadow-xl"
        >
          {panel === 'root' ? (
            <>
              <Item icon={<Icons.Folder />} label="New folder" onSelect={run(onNewFolder)} />

              <Rule />

              <Item icon={<Icons.Upload />} label="File upload" onSelect={run(onUploadFiles)} />
              {/*
                The browser puts its own confirmation in front of a folder
                upload — "this will upload all files from …" — drawn outside
                the page where nothing here can restyle or replace it. It is a
                security prompt, and every site that reads a directory gets
                it. Saying so up front is all we can do, and it keeps the
                thing from reading as our dialog gone wrong.
              */}
              <Item
                icon={<Icons.Folder />}
                label="Folder upload"
                detail="Your browser will ask you to confirm"
                onSelect={run(onUploadFolder)}
              />

              <Rule />

              <Item
                icon={<Icons.Sheet />}
                label="Excel sheet"
                detail="Blank, a template, or import a workbook"
                onSelect={() => setPanel('sheet')}
                more
              />
              <Item
                icon={<Icons.File />}
                label="Word document"
                detail="Blank, or a letterhead"
                onSelect={() => setPanel('document')}
                more
              />
            </>
          ) : null}

          {panel === 'sheet' ? (
            <>
              <Back label="Excel sheet" onBack={() => setPanel('root')} />
              {/* No separate "Blank sheet" entry: the template list already
                  opens with one, and offering both left two ways to do the
                  same thing sitting on top of each other. */}
              {(sheets.templates.data ?? []).map((template) => (
                <Item
                  key={template.id}
                  {...(template.id === 'blank' ? { icon: <Icons.Sheet /> } : {})}
                  label={template.name}
                  detail={template.description}
                  onSelect={run(() =>
                    sheets.createFrom({
                      title: template.name,
                      suggested: template.name,
                      template: template.id,
                    }),
                  )}
                />
              ))}
              {sheets.templates.isLoading ? <Loading /> : null}

              <Rule />
              <Item
                icon={<Icons.Upload />}
                label="Import a workbook…"
                detail=".xlsx, .xls, .csv or .ods"
                onSelect={() => {
                  close();
                  importInput.current?.click();
                }}
              />
            </>
          ) : null}

          {panel === 'document' ? (
            <>
              <Back label="Word document" onBack={() => setPanel('root')} />
              {(documents.templates.data ?? []).map((template) => (
                <Item
                  key={template.id}
                  icon={template.id === 'blank' ? <Icons.File /> : undefined}
                  label={template.name}
                  detail={template.description}
                  onSelect={run(() => documents.createFrom(template))}
                />
              ))}
              {documents.templates.isLoading ? <Loading /> : null}

              <Rule />
              <p className="px-3 py-2 text-micro text-muted">
                To open a Word file, upload it — a .docx becomes an editable document by itself.
              </p>
            </>
          ) : null}
        </div>
      ) : null}

      {/*
        The workbook importer's own picker, kept here so "Import a workbook"
        behaves like the menu item it looks like rather than opening the
        general upload and hoping.
      */}
      <input
        ref={importInput}
        type="file"
        multiple
        accept=".xlsx,.xls,.csv,.ods,.xlsm"
        className="hidden"
        onChange={(event) => {
          const chosen = Array.from(event.target.files ?? []);
          event.target.value = '';
          void sheets.importFiles(chosen);
        }}
      />
    </div>
  );
}

function Item({
  icon,
  label,
  detail,
  onSelect,
  more,
}: {
  icon?: ReactNode;
  label: string;
  detail?: string | undefined;
  onSelect(): void;
  more?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className="flex w-full items-center gap-3 px-3 py-2 text-left transition-fast hover:bg-sunken"
    >
      {/* A fixed slot whether or not there is a glyph, so the labels line up
          down the menu rather than stepping in and out. */}
      <span className="flex w-4 shrink-0 justify-center text-muted">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body-sm text-ink">{label}</span>
        {detail ? <span className="block truncate text-micro text-muted">{detail}</span> : null}
      </span>
      {more ? (
        <span className="text-[10px] text-muted" aria-hidden>
          ▸
        </span>
      ) : null}
    </button>
  );
}

function Back({ label, onBack }: { label: string; onBack(): void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className={cx(
        'mb-1 flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left',
        'text-micro font-semibold uppercase tracking-wide text-muted transition-fast hover:bg-sunken',
      )}
    >
      <span aria-hidden>←</span>
      {label}
    </button>
  );
}

function Rule() {
  return <div className="my-1 h-px bg-[var(--border)]" />;
}

function Loading() {
  return <p className="px-3 py-2 text-body-sm text-muted">Loading templates…</p>;
}
